// src/redux/slices/bulkTransfersSlice.js
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { message, notification } from "antd";
import dayjs from "dayjs";
import {
  getFlowToCustomer,
  getItemsDetail,
  PostBulkTransfersIn,
  PostBulkTransfersOut,
  getBaseDocumentsStream,
} from "../../config/Services/Userservices";
import { validateBulkTransferEntries } from "../../utils/validationUtils"

// Define constant for page size
const DEFAULT_PAGE_SIZE = 500;

const parseCSVResponse = (csvString) => {
  const rows = csvString.trim().split("\n");
  const header = rows[0].split(",");
  if (
    header.length === 4 &&
    header[0] === "Status" &&
    header[1] === "OrderNo" &&
    header[2] === "InvoiceNo" &&
    header[3] === "Message"
  ) {
    return rows.slice(1).map((row) => {
      const [Status, OrderNo, InvoiceNo, Message] = row.split(",");
      return { Status, OrderNo, InvoiceNo, Message };
    });
  } else if (
    header.length === 6 &&
    header[0] === "MOVEMENT_DOC" &&
    header[1] === "TRANSFER_TYPE" &&
    header[2] === "TRO_DOC_NO" &&
    header[3] === "TRI_DOC_NO" &&
    header[4] === "STATUS" &&
    header[5] === "MESSAGE"
  ) {
    return rows.slice(1).map((row) => {
      const [
        MOVEMENT_DOC,
        TRANSFER_TYPE,
        TRO_DOC_NO,
        TRI_DOC_NO,
        STATUS,
        MESSAGE,
      ] = row.split(",");
      return {
        MOVEMENT_DOC,
        TRANSFER_TYPE,
        TRO_DOC_NO,
        TRI_DOC_NO,
        STATUS,
        MESSAGE,
      };
    });
  }
  return [];
};

// Helper function to clean Dayjs objects from data
const cleanDayjsObjects = (data) => {
  if (!data && data !== false) return data;

  // Handle primitive types
  if (typeof data !== 'object') {
    return data;
  }

  // If it's already a Dayjs object
  if (dayjs.isDayjs && dayjs.isDayjs(data)) {
    return data.format("YYYY-MM-DD");
  }

  // Check for common Dayjs properties
  if (data && typeof data === 'object' && ('$L' in data || '$y' in data || '$M' in data)) {
    try {
      // Try to parse as date
      const parsed = dayjs(data);
      if (parsed.isValid()) {
        return parsed.format("YYYY-MM-DD");
      }
    } catch (error) {
      console.warn('Failed to parse Dayjs object:', data);
    }
  }

  // Handle arrays
  if (Array.isArray(data)) {
    return data.map(item => cleanDayjsObjects(item));
  }

  // Handle objects
  const cleaned = { ...data };

  Object.keys(cleaned).forEach(key => {
    const value = cleaned[key];

    // Skip if value is null or undefined
    if (value === null || value === undefined) {
      return;
    }

    // Handle boolean values
    if (typeof value === 'boolean') {
      cleaned[key] = value;
      return;
    }

    // Handle string values (including empty strings)
    if (typeof value === 'string') {
      cleaned[key] = value.trim();
      return;
    }

    // Handle date strings
    if (key.includes('DATE') || key.includes('Date')) {
      if (typeof value === 'string' && value.trim() !== '') {
        try {
          // Try to parse the date string
          const formats = ['YYYY-MM-DD', 'DD/MM/YYYY', 'MM/DD/YYYY', 'YYYY/MM/DD'];
          for (const format of formats) {
            const parsed = dayjs(value, format);
            if (parsed.isValid()) {
              cleaned[key] = parsed.format('YYYY-MM-DD');
              break;
            }
          }
        } catch (error) {
          console.warn('Failed to parse date string:', key, value);
        }
      }
      return;
    }

    // Recursively clean nested objects
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      cleaned[key] = cleanDayjsObjects(value);
    }
  });

  return cleaned;
};

export const fetchToCustomers = createAsyncThunk(
  "bulkTransfers/fetchToCustomers",
  async (customerCode, { rejectWithValue }) => {
    try {
      const bpCode = localStorage.getItem("bpCode");
      const payload = {
        Request_Data: {
          Customer_No: customerCode || bpCode,
        },
      };
      const response = await getFlowToCustomer(payload);
      if (response.status === 200) {
        const responseData = response?.data?.Customer_Data;

        if (responseData?.Item && !Array.isArray(responseData?.Item)) {
          responseData.Item = [responseData.Item];
        }
        return response.data;
      } else {
        message.error("Failed to fetch To Customers");
        console.error("Fetch To Customers failed:", response.data);
        return rejectWithValue("Failed to fetch To Customers");
      }
    } catch (error) {
      console.error("Fetch To Customers failed:", error);
      return rejectWithValue(error);
    }
  }
);

export const fetchItemDetails = createAsyncThunk(
  "bulkTransfers/fetchItemDetails",
  async (toCustomerCode, { rejectWithValue }) => {
    try {
      const bpCode = localStorage.getItem("bpCode");
      const payload = {
        Request_Data: {
          From_Customer: bpCode,
          To_Customer: toCustomerCode,
        },
      };
      const response = await getItemsDetail(payload);
      if (response.status === 200) {
        return response.data;
      } else {
        message.error("Failed to fetch To Item Details");
        console.error("Fetch To Item Details failed:", response.data);
        return rejectWithValue("Failed to fetch To Item Details");
      }
    } catch (error) {
      console.error("Fetch Item Details failed:", error);
      return rejectWithValue(error);
    }
  }
);

export const PostBulkTransfersOUT = createAsyncThunk(
  "bulkTransfers/PostBulkTransfersOUT",
  async (
    { payload, setData, setexcelData, setDropdowns },
    { rejectWithValue, dispatch }
  ) => {
    try {
      const items = payload?.Request_Data?.items || [];

      const validationErrors = validateBulkTransferEntries(items);

      if (validationErrors.length > 0) {
        notification.error({
          message: "Validation Failed",
          description: (
            <div>
              <p>Please fix the following errors:</p>
              <ul>
                {validationErrors.slice(0, 5).map((error, index) => (
                  <li key={index}>Row {error.row}: {error.message}</li>
                ))}
                {validationErrors.length > 5 && (
                  <li>...and {validationErrors.length - 5} more errors</li>
                )}
              </ul>
            </div>
          ),
          placement: "topRight",
          duration: 10,
        });

        return rejectWithValue(validationErrors);
      }

      const groupedByInvoice = {};
      console.log("🔍 GROUPING - Input items count:", items.length);
      console.log("🔍 GROUPING - Input items:", items.map((item, i) => ({
        index: i,
        Invoice_No: item.HEADER?.Invoice_No,
        To_Customer: item.HEADER?.To_Customer,
        BP_Code: item.HEADER?.BP_Code,
        hasItems: !!item.ITEMS?.item,
        itemsCount: item.ITEMS?.item?.length || 0
      })));

      items.forEach((item, index) => {
        const invoiceNo = item.HEADER?.Invoice_No;
        const toCustomer = item.HEADER?.To_Customer;
        // Create composite key: Invoice_No + To_Customer
        const groupKey = `${invoiceNo}|${toCustomer}`;
        console.log(`📝 Processing item ${index}: Invoice_No = "${invoiceNo}", To_Customer = "${toCustomer}", GroupKey = "${groupKey}"`);
        if (!invoiceNo) {
          console.warn(`⚠️ Item ${index} skipped - no Invoice_No`);
          return;
        }

        if (!groupedByInvoice[groupKey]) {
          console.log(`✨ Creating new group for key: "${groupKey}" (Invoice: "${invoiceNo}", To_Customer: "${toCustomer}")`);
          groupedByInvoice[groupKey] = {
            HEADER: { ...item.HEADER },
            ITEMS: { item: [] }
          };
        }

        if (item.ITEMS?.item) {
          const itemsArray = Array.isArray(item.ITEMS.item)
            ? item.ITEMS.item
            : [item.ITEMS.item];
          console.log(`➕ Adding ${itemsArray.length} items to group "${groupKey}"`);
          groupedByInvoice[groupKey].ITEMS.item.push(...itemsArray);
        }
      });

      console.log("📊 GROUPING RESULT - Groups created:", Object.keys(groupedByInvoice).length);
      console.log("📊 Group keys:", Object.keys(groupedByInvoice));

      const groupedItems = Object.values(groupedByInvoice).map(group => {
        if (group.ITEMS?.item && Array.isArray(group.ITEMS.item)) {
          group.ITEMS.item = group.ITEMS.item.map((item, index) => ({
            ...item,
            ItemNo: String((index + 1) * 10).padStart(4, '0')
          }));
        }
        return group;
      });

      const processedPayload = {
        ...payload,
        Request_Data: {
          ...payload.Request_Data,
          items: groupedItems
        }
      };

      const response = await PostBulkTransfersOut(processedPayload);

      if (response.status === 200) {
        const responseData = response?.data;

        if (
          typeof responseData === "string" &&
          responseData.includes("Status,OrderNo,InvoiceNo,Message")
        ) {
          const parsedData = parseCSVResponse(responseData);

          dispatch(showModal(parsedData));
          setData([]);
          setexcelData([]);
          setDropdowns({
            toCustomerOptions: {},
            addressMap: {},
            skuOptions: {},
          });

          return response.data;
        }

        if (response?.data?.success && response?.data?.jobId) {
          return { jobId: response.data.jobId, message: response.data.message, STATUS: "S" };
        }

        // Safely extract message string from response data
        let successMessage = "Bulk Transfers OUT processed successfully";

        if (typeof responseData === 'string') {
          successMessage = responseData;
        } else if (responseData?.message) {
          successMessage = responseData.message;

          // Add counts if available in the response
          if (responseData.count !== undefined || responseData.successCount !== undefined) {
            const counts = [];
            if (responseData.count !== undefined) counts.push(`Total: ${responseData.count}`);
            if (responseData.successCount !== undefined) counts.push(`Success: ${responseData.successCount}`);
            if (responseData.duplicateCount !== undefined) counts.push(`Duplicates: ${responseData.duplicateCount}`);

            if (counts.length > 0) {
              successMessage += ` (${counts.join(", ")})`;
            }
          }
        } else if (responseData?.Message) {
          successMessage = responseData.Message;
        }

        notification.success({
          message: "Success",
          description: successMessage,
          placement: "topRight",
          duration: 15
        });

        // Use message.success as well for extra visibility
        message.success(successMessage);

        return response.data;
      } else {

        let errorMsg = "Failed to Post Bulk Transfers OUT";

        if (Array.isArray(response?.data?.errors)) {
          errorMsg = response.data.errors.map((e) => e.message).join(", ");
        } else if (response?.data) {
          console.error("❌ API Error Response:", response.data);
        }

        notification.error({
          message: "Error",
          description: errorMsg,
          placement: "topRight",
        });
        return rejectWithValue(errorMsg);
      }
    } catch (error) {
      console.error("❌ Exception in PostBulkTransfersOUT:", error);

      notification.error({
        message: "Error",
        description: error?.message || "Unknown error occurred",
        placement: "topRight",
      });
      return rejectWithValue(error);
    }
  }
);

export const getBaseDocs = createAsyncThunk(
  "bulkTransfers/getBaseDocs",
  async ({
    customerNo,
    page = 1,
    pageSize = DEFAULT_PAGE_SIZE,
    isLoadMore = false,
    refreshOnly = false,
    fromDate = null,
    toDate = null,
    documents = []
  }, { rejectWithValue, getState, signal }) => {
    try {
      const fromIndex = ((page - 1) * pageSize + 1).toString();
      const toIndex = (page * pageSize).toString();

      const payload = {
        Request_Data: {
          CustomerNo: customerNo,
          FromIndex: fromIndex,
          ToIndex: toIndex,
          TotalRecord: "",
          _: Date.now(),
        },
      };

      // Add filter parameters if provided
      if (fromDate) {
        payload.Request_Data.FromDate = dayjs(fromDate).format("MM/DD/YYYY");
      } else {
        payload.Request_Data.FromDate = "";
      }

      if (toDate) {
        payload.Request_Data.ToDate = dayjs(toDate).format("MM/DD/YYYY");
      } else {
        payload.Request_Data.ToDate = "";
      }

      if (documents && documents.length > 0) {
        payload.Request_Data.Documents = {
          Item: documents.map(doc => ({ Document: doc }))
        };
      } else {
        payload.Request_Data.Documents = "";
      }

      console.log('📤 getBaseDocs API Payload:', JSON.stringify(payload, null, 2));

      const response = await getBaseDocumentsStream(payload);

      if (response.status === 200) {
        const loggedInUser = localStorage.getItem("bpCode")?.trim();
        const paddedLoggedInUser = loggedInUser?.padStart(10, "0");

        let responseData = response?.data?.Response_Data;
        const receivedCount = responseData?.Item?.length || 0;

        // CRITICAL FIX: Only hasMore if we received EXACTLY pageSize items
        const hasMore = receivedCount === pageSize;

        if (!responseData) {
          return {
            Response_Data: { Item: [] },
            TotalCount: 0,
            CurrentPage: page,
            PageSize: pageSize,
            HasMore: false,
            IsLoadMore: isLoadMore,
            RefreshOnly: refreshOnly,
            IsEmpty: true
          };
        }

        if (responseData.Item && !Array.isArray(responseData.Item)) {
          responseData.Item = [responseData.Item];
        }

        if (responseData.Item) {
          responseData.Item = responseData.Item
            .map((item) => {
              if (item.ITEMS && item.ITEMS.item && !Array.isArray(item.ITEMS.item)) {
                item.ITEMS.item = [item.ITEMS.item];
              }
              return item;
            })
            .filter(item => {
              const sender = item.SENDER?.trim();
              const paddedSender = sender?.padStart(10, "0");

              return sender !== loggedInUser &&
                paddedSender !== paddedLoggedInUser &&
                sender !== paddedLoggedInUser &&
                paddedSender !== loggedInUser;
            });
        }

        // Add keys to items for React rendering
        const itemsWithKeys = (responseData.Item || []).map((item, index) => ({
          ...item,
          key: item.DOCUMENT_NO || `${page}-${index}`,
          serial: item.serial || ((page - 1) * pageSize + index + 1)
        }));

        return {
          Response_Data: {
            Item: itemsWithKeys || []
          },
          TotalCount: receivedCount,
          CurrentPage: page,
          PageSize: pageSize,
          HasMore: hasMore,
          IsLoadMore: isLoadMore,
          RefreshOnly: refreshOnly,
          IsEmpty: receivedCount === 0
        };
      } else {
        return {
          Response_Data: { Item: [] },
          TotalCount: 0,
          CurrentPage: page,
          PageSize: pageSize,
          HasMore: false,
          IsLoadMore: isLoadMore,
          RefreshOnly: refreshOnly,
          IsEmpty: true
        };
      }
    } catch (error) {
      console.error("Fetching Base Documents failed:", error);
      return rejectWithValue(error.message || 'Unknown error');
    }
  }
);

export const PostBulkTransfersIN = createAsyncThunk(
  "bulkTransfers/PostBulkTransfersIN",
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      const response = await PostBulkTransfersIn(payload);

      if (response.status === 200) {
        let message = response?.data;

        // Check if response is CSV string
        if (
          typeof message === "string" &&
          message.includes("MOVEMENT_DOC,TRANSFER_TYPE,TRO_DOC_NO,TRI_DOC_NO,STATUS,MESSAGE")
        ) {
          const parsedData = parseCSVResponse(message);

          // CRITICAL: Ensure parsedData is an array
          const dataArray = Array.isArray(parsedData) ? parsedData : [parsedData];

          dispatch(showModal(dataArray));

          const bpCode = localStorage.getItem("bpCode");
          // Update with proper pagination parameters
          dispatch(getBaseDocs({
            customerNo: bpCode.padStart(10, "0"),
            page: 1,
            pageSize: DEFAULT_PAGE_SIZE,
            isLoadMore: false,
            refreshOnly: true // IMPORTANT: Add refreshOnly flag
          }));

          return dataArray;
        }

        // Check if response is a success message object/string
        // The user says response is: {"success": true, "message": "Successfully uploaded 1 dockets..."}
        if (response?.data?.success && response?.data?.jobId) {
          const successMsg = response.data.message;
          const jobId = response.data.jobId;
          
          return { jobId, message: successMsg, STATUS: "S" };
        }

        // Check if response is an error string
        const isErrorString =
          typeof message === "string" &&
          (message.toLowerCase().includes("error") ||
            message.toLowerCase().includes("failed") ||
            message.startsWith("E,"));

        if (isErrorString) {
          const errorMessage = typeof message === 'string' ? message : (message?.message || "Transfer failed");

          notification.error({
            message: "Error",
            description: errorMessage,
            placement: "topRight",
          });
          return rejectWithValue([{ STATUS: "E", MESSAGE: errorMessage }]);
        }

        // Handle success response - Safely extract message
        const finalSuccessMsg = typeof message === 'string' ? message : (message?.message || "Bulk Transfers IN posted successfully");

        notification.success({
          message: "Success",
          description: finalSuccessMsg,
          placement: "topRight",
        });

        // Return success response as array
        return [{ STATUS: "S", MESSAGE: finalSuccessMsg }];
      } else {
        notification.error({
          message: "Error",
          description: "Failed to Post Bulk Transfers IN",
          placement: "topRight",
        });
        return rejectWithValue([{ STATUS: "E", MESSAGE: "Failed to Post Bulk Transfers IN" }]);
      }
    } catch (error) {
      console.error("Posting Bulk Transfers IN failed:", error);
      notification.error({
        message: "Error",
        description: error?.message || "Unknown error occurred",
        placement: "topRight",
      });
      return rejectWithValue([{ STATUS: "E", MESSAGE: error?.message || "Unknown error" }]);
    }
  }
);


const bulkTransfersSlice = createSlice({
  name: "bulkTransfers",
  initialState: {
    toCustomersloading: false,
    toCustomersError: null,
    toCustomers: [],

    itemDetailsLoading: false,
    itemDetailsError: null,
    itemDetails: {},

    postBulkTransfersOUTLoading: false,
    postBulkTransfersOUTError: null,
    postBulkTransfersOUTResponse: {},

    baseDocumentsLoading: false,
    baseDocumentsError: null,
    baseDocuments: [],

    // NEW: Pagination state
    baseDocumentsPagination: {
      currentPage: 1,
      pageSize: DEFAULT_PAGE_SIZE,
      totalCount: 0,
      hasMore: false,
      totalPages: 0,
    },

    postBulkTransfersINLoading: false,
    postBulkTransfersINError: null,
    postBulkTransfersINResponse: {},

    bulkTransferINData: [],
    selectedBulkItems: [],
    uploadedData: [],

    modalVisible: false,
    modalData: [],
  },
  reducers: {
    showModal: (state, action) => {
      state.modalVisible = true;
      state.modalData = action.payload;
    },
    hideModal: (state) => {
      state.modalVisible = false;
      state.modalData = [];
    },
    removeBulkTransferINDataByKeys: (state, action) => {
      const keysToRemove = action.payload;
      if (Array.isArray(keysToRemove) && keysToRemove.length > 0) {
        state.bulkTransferINData = state.bulkTransferINData.filter(item => !keysToRemove.includes(item.key));
        state.baseDocuments = state.baseDocuments.filter(item => !keysToRemove.includes(item.key));
        state.uploadedData = state.uploadedData.filter(item => !keysToRemove.includes(item.key));
        state.selectedBulkItems = state.selectedBulkItems.filter(item => !keysToRemove.includes(item.key));
      }
    },
    setBulkTransferINData: (state, action) => {
      if (Array.isArray(action.payload)) {
        // Clean any Dayjs objects from the data
        const cleanedData = action.payload.map(item => cleanDayjsObjects(item));

        state.bulkTransferINData = cleanedData.map(item => ({ ...item }));
      } else {
        console.error('setBulkTransferINData called with non-array payload:', action.payload);
        state.bulkTransferINData = [];
      }
    },
    clearBulkTransferINData: (state) => {
      state.bulkTransferINData = [];
    },
    clearUploadedData: (state) => {
      state.uploadedData = [];
    },
    setUploadedData: (state, action) => {
      if (Array.isArray(action.payload)) {
        // Clean any Dayjs objects from the data
        const cleanedData = action.payload.map(item => cleanDayjsObjects(item));

        state.uploadedData = cleanedData.map(item => ({ ...item }));
      } else {
        console.error('setUploadedData called with non-array payload:', action.payload);
        state.uploadedData = [];
      }
    },

    updateBulkTransferItem: (state, action) => {
      const { key, field, value } = action.payload;

      // Helper function to find item index
      const findItemIndex = (array) => array.findIndex(item => item.key === key);

      // Update in bulkTransferINData
      const itemIndex = findItemIndex(state.bulkTransferINData);
      if (itemIndex !== -1) {
        // Clean value if it's a Dayjs object
        const cleanedValue = cleanDayjsObjects(value);

        state.bulkTransferINData[itemIndex] = {
          ...state.bulkTransferINData[itemIndex],
          [field]: cleanedValue
        };
      } else {
        const baseDoc = state.baseDocuments.find(item => item.key === key);
        if (baseDoc) {
          // Clean value if it's a Dayjs object
          const cleanedValue = cleanDayjsObjects(value);

          state.bulkTransferINData.push({
            ...baseDoc,
            [field]: cleanedValue
          });
        }
      }

      // Update in baseDocuments
      const baseDocIndex = findItemIndex(state.baseDocuments);
      if (baseDocIndex !== -1) {
        // Clean value if it's a Dayjs object
        const cleanedValue = cleanDayjsObjects(value);

        state.baseDocuments[baseDocIndex] = {
          ...state.baseDocuments[baseDocIndex],
          [field]: cleanedValue
        };
      }

      // Update in selectedBulkItems
      const selectedIndex = findItemIndex(state.selectedBulkItems);
      if (selectedIndex !== -1) {
        // Clean value if it's a Dayjs object
        const cleanedValue = cleanDayjsObjects(value);

        state.selectedBulkItems[selectedIndex] = {
          ...state.selectedBulkItems[selectedIndex],
          [field]: cleanedValue
        };
      }

      // Update in uploadedData
      const uploadedIndex = findItemIndex(state.uploadedData);
      if (uploadedIndex !== -1) {
        // Clean value if it's a Dayjs object
        const cleanedValue = cleanDayjsObjects(value);

        state.uploadedData[uploadedIndex] = {
          ...state.uploadedData[uploadedIndex],
          [field]: cleanedValue
        };
      }
    },
    setBaseDocuments: (state, action) => {
      if (Array.isArray(action.payload)) {
        // Clean any Dayjs objects from the data
        const cleanedData = action.payload.map(item => cleanDayjsObjects(item));

        state.baseDocuments = cleanedData.map(item => ({ ...item }));
      } else {
        console.error('setBaseDocuments called with non-array payload:', action.payload);
        state.baseDocuments = [];
      }
    },
    setSelectedBulkItems: (state, action) => {
      if (Array.isArray(action.payload)) {
        // Clean any Dayjs objects from the data
        const cleanedData = action.payload.map(item => cleanDayjsObjects(item));

        state.selectedBulkItems = cleanedData.map(item => ({ ...item }));
      } else {
        console.error('setSelectedBulkItems called with non-array payload:', action.payload);
        state.selectedBulkItems = [];
      }
    },
    addSelectedBulkItem: (state, action) => {
      const existingIndex = state.selectedBulkItems.findIndex(
        item => item.key === action.payload.key
      );
      if (existingIndex === -1) {
        // Clean any Dayjs objects from the data
        const cleanedItem = cleanDayjsObjects(action.payload);
        state.selectedBulkItems.push({ ...cleanedItem });
      }
    },
    removeSelectedBulkItem: (state, action) => {
      state.selectedBulkItems = state.selectedBulkItems.filter(
        item => item.key !== action.payload
      );
    },
    clearSelectedBulkItems: (state) => {
      state.selectedBulkItems = [];
    },

    // NEW: Pagination reducers
    setBaseDocumentsPagination: (state, action) => {
      state.baseDocumentsPagination = {
        ...state.baseDocumentsPagination,
        ...action.payload
      };
    },

    resetBaseDocumentsPagination: (state) => {
      state.baseDocumentsPagination = {
        currentPage: 1,
        pageSize: DEFAULT_PAGE_SIZE,
        totalCount: 0,
        hasMore: false,
        totalPages: 0,
      };
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchToCustomers.pending, (state) => {
        state.toCustomersloading = true;
        state.toCustomersError = null;
      })
      .addCase(fetchToCustomers.fulfilled, (state, action) => {
        state.toCustomersloading = false;
        state.toCustomers = [...(action?.payload?.Customer_Data?.Item || [])];
      })
      .addCase(fetchToCustomers.rejected, (state, action) => {
        state.toCustomersloading = false;
        state.toCustomersError = action.payload;
      })
      .addCase(fetchItemDetails.pending, (state) => {
        state.itemDetailsLoading = true;
        state.itemDetailsError = null;
      })
      .addCase(fetchItemDetails.fulfilled, (state, action) => {
        state.itemDetailsLoading = false;
        state.itemDetails = [...(action?.payload?.Response_Data?.Item || [])];
      })
      .addCase(fetchItemDetails.rejected, (state, action) => {
        state.itemDetailsLoading = false;
        state.itemDetailsError = action.payload;
      })
      .addCase(PostBulkTransfersOUT.pending, (state) => {
        state.postBulkTransfersOUTLoading = true;
        state.postBulkTransfersOUTError = null;
      })
      .addCase(PostBulkTransfersOUT.fulfilled, (state, action) => {
        state.postBulkTransfersOUTLoading = false;
        state.postBulkTransfersOUTResponse = action?.payload || "";
        state.uploadedData = [];
      })
      .addCase(PostBulkTransfersOUT.rejected, (state, action) => {
        state.postBulkTransfersOUTLoading = false;
        state.postBulkTransfersOUTError = action.payload;
      })
      .addCase(PostBulkTransfersIN.pending, (state) => {
        state.postBulkTransfersINLoading = true;
        state.postBulkTransfersINError = null;
      })
      .addCase(PostBulkTransfersIN.fulfilled, (state, action) => {
        state.postBulkTransfersINLoading = false;
        state.postBulkTransfersINResponse = action?.payload || "";
        state.selectedBulkItems = [];
      })
      .addCase(PostBulkTransfersIN.rejected, (state, action) => {
        state.postBulkTransfersINLoading = false;
        state.postBulkTransfersINError = action.payload;
      })
      .addCase(getBaseDocs.pending, (state) => {
        state.baseDocumentsLoading = true;
        state.baseDocumentsError = null;
      })
      // ========== FIXED: Updated getBaseDocs.fulfilled handler ==========
      .addCase(getBaseDocs.fulfilled, (state, action) => {
        state.baseDocumentsLoading = false;

        const items = action.payload?.Response_Data?.Item || [];
        const currentPage = action.payload?.CurrentPage || 1;
        const hasMore = action.payload?.HasMore || false;
        const isLoadMore = action.payload?.IsLoadMore || false;
        const refreshOnly = action.payload?.RefreshOnly || false;

        if (items.length === 0) {
          if (currentPage === 1 || !isLoadMore) {
            if (!refreshOnly) {
              state.baseDocuments = [];
              state.bulkTransferINData = [];
            }
          }
        } else {
          const itemsArray = Array.isArray(items) ? items : [items];
          const processedData = itemsArray.map((item, index) => ({
            ...cleanDayjsObjects(item),
            key: item.key || item.DOCUMENT_NO || `${currentPage}-${index}`,
            serial: item.serial || ((currentPage - 1) * DEFAULT_PAGE_SIZE + index + 1)
          }));

          if (refreshOnly) {
            // IMPORTANT: For refreshOnly, only update page 1 items
            // Keep items from other pages
            const page1Keys = new Set(processedData.map(item => item.DOCUMENT_NO));

            // Filter out existing page 1 items
            const otherPagesData = state.baseDocuments.filter(item => !page1Keys.has(item.DOCUMENT_NO));

            // Combine refreshed page 1 with existing other pages
            state.baseDocuments = [...processedData, ...otherPagesData];

            // Same for bulkTransferINData
            const otherPagesBulkData = state.bulkTransferINData.filter(item => !page1Keys.has(item.DOCUMENT_NO));
            state.bulkTransferINData = [...processedData, ...otherPagesBulkData];
          } else if (currentPage === 1 || !isLoadMore) {
            // Normal replacement for first page
            state.baseDocuments = processedData;
            state.bulkTransferINData = processedData;
          } else {
            // Loading more - append new data
            const existingKeys = new Set((state.baseDocuments || []).map(doc => doc?.DOCUMENT_NO));
            const newItems = processedData.filter(doc => !existingKeys.has(doc.DOCUMENT_NO));

            state.baseDocuments = [...(state.baseDocuments || []), ...newItems];

            // Append to bulkTransferINData as well
            const existingBulkKeys = new Set((state.bulkTransferINData || []).map(doc => doc?.DOCUMENT_NO));
            const newBulkItems = processedData.filter(doc => !existingBulkKeys.has(doc.DOCUMENT_NO));
            state.bulkTransferINData = [...(state.bulkTransferINData || []), ...newBulkItems];
          }
        }

        // Update pagination state
        state.baseDocumentsPagination = {
          ...state.baseDocumentsPagination,
          currentPage,
          hasMore: refreshOnly ? (state.baseDocuments.length > DEFAULT_PAGE_SIZE || hasMore) : hasMore,
          totalCount: state.baseDocuments.length
        };
      })
      .addCase(getBaseDocs.rejected, (state, action) => {
        state.baseDocumentsLoading = false;
        state.baseDocumentsError = action.payload;
        // Only clear if it was a first page load
        if (state.baseDocumentsPagination.currentPage === 1) {
          state.baseDocuments = [];
          state.bulkTransferINData = [];
          state.selectedBulkItems = [];
        }
      });
  },
});

export const {
  showModal,
  hideModal,
  removeBulkTransferINDataByKeys,
  setBulkTransferINData,
  clearBulkTransferINData,
  clearUploadedData,
  setUploadedData,
  updateBulkTransferItem,
  setBaseDocuments,
  setSelectedBulkItems,
  addSelectedBulkItem,
  removeSelectedBulkItem,
  clearSelectedBulkItems,
  setBaseDocumentsPagination,
  resetBaseDocumentsPagination
} = bulkTransfersSlice.actions;

export default bulkTransfersSlice.reducer;